import { Dialog, Text, Button, Group } from '@mantine/core';
import { ReactElement, useEffect, useState } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type { Practitioner } from '@medplum/fhirtypes';
import { fetchBot } from '../services/services';
import { usePostHog } from 'posthog-js/react';
import { getReferenceString } from '@medplum/core';

const notificationPosthogPayload = (profile: Practitioner | undefined) => ({
  provider: profile ? getReferenceString(profile) : undefined,
  timestamp: new Date().toISOString(),
  browser: navigator.userAgent,
});

/**
 * Soft prompt shown after login to inform users they can enable push notifications.
 * Uses the Push API with service workers for reliable message delivery.
 * It auto-shows when:
 * - Browser supports Push API and Service Workers
 * - Notification permission is 'default' (not granted/denied)
 * - The user hasn't dismissed the prompt recently (localStorage TTL)
 */
export function PushNotificationsPrompt(): ReactElement | null {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const posthog = usePostHog();

  const [opened, setOpened] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const supportsPushAPI = 'serviceWorker' in navigator && 'PushManager' in window;
    const supportsNotifications = 'Notification' in window;

    // Respect prior dismissals with a TTL (e.g., 7 days)
    const raw = localStorage.getItem('pushPromptDismissedUntil');
    const dismissedUntil = raw ? Number(raw) : 0;
    const now = Date.now();

    if (supportsPushAPI && supportsNotifications && now > dismissedUntil) {
      const permission = Notification.permission;
      if (permission === 'default') {
        setOpened(true);
      }
    }
  }, []);

  const dismiss = () => {
    setOpened(false);
    // Do not nag for 7 days
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    localStorage.setItem('pushPromptDismissedUntil', String(Date.now() + sevenDaysMs));
  };

  const urlBase64ToUint8Array = (base64: string): Uint8Array => {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const base64Safe = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64Safe);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
    return output;
  };

  const enablePushNotifications = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      dismiss();
      return;
    }

    try {
      setBusy(true);
      posthog.capture('notification permission requested', notificationPosthogPayload(profile));
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        posthog.capture('notification permission denied', notificationPosthogPayload(profile));
        // Respect user choice; shorter cooldown on deny/dismiss (24h)
        const oneDayMs = 24 * 60 * 60 * 1000;
        localStorage.setItem('pushPromptDismissedUntil', String(Date.now() + oneDayMs));
        setOpened(false);
        return;
      }

      // Register service worker and create push subscription
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        posthog.capture('notification permission granted', notificationPosthogPayload(profile));
        try {
          const registration = await navigator.serviceWorker.register('/push-notification-service-worker.js');

          // Wait for service worker to be ready
          await navigator.serviceWorker.ready;

          const vapidPublicKey = import.meta.env?.VITE_VAPID_PUBLIC_KEY as string | undefined;
          if (!vapidPublicKey) {
            // eslint-disable-next-line no-console
            console.warn('VAPID public key not configured. Push notifications will not work.');
            setOpened(false);
            return;
          }

          const subscription = await registration.pushManager.subscribe({
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey).buffer as ArrayBuffer,
            userVisibleOnly: true,
          });

          // Send subscription to Medplum via bot, attached to Practitioner
          if (medplum && profile?.id) {
            try {
              await fetchBot(medplum, 'save-push-subscription', {
                practitionerId: profile.id,
                subscription,
              });
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('Failed to save push subscription via bot', err);
            }
          }
        } catch (err) {
          // Service worker registration or push subscription failed
          // eslint-disable-next-line no-console
          console.error('Failed to set up push notifications:', err);
          posthog.capture('notification error', {
            ...notificationPosthogPayload(profile),
            error: err instanceof Error ? err.message : "Couldn't set up push notifications",
            errorType: 'subscription_failed',
          });
        }
      }

      setOpened(false);
    } finally {
      setBusy(false);
    }
  };

  if (!opened) {
    return null;
  }

  return (
    <Dialog
      opened={opened}
      onClose={dismiss}
      withCloseButton
      position={{ bottom: 16, right: 16 }}
      radius="md"
      shadow="md"
    >
      <Text fw={600} mb="xs">
        Never miss calls or tasks
      </Text>
      <Text mb="sm">
        Turn on browser notifications to get real-time notifications about incoming patient calls/messages and tasks
        assigned to you.
      </Text>
      <Group justify="flex-end">
        <Button onClick={enablePushNotifications} loading={busy}>
          Enable notifications
        </Button>
        <Button variant="outline" onClick={dismiss}>
          Later
        </Button>
      </Group>
    </Dialog>
  );
}
